import {useState} from 'react'
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT} from '#/lib/slideTokens'
import {PlayScroller} from '#/components/capsules/play/PlayScroller'
import {
  OptionMarker,
  QuestionHeading,
} from '#/components/capsules/play/ThemeBits'
import {type SlidePlayProps} from '#/components/capsules/play/types'
import {
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  useInkStroke,
  usePlayAccent,
  usePlayContentPadding,
  usePlayOptionListStyle,
  usePlayOptionRowRhythm,
  usePlayOptionStyle,
  usePlayOptionTextStyle,
  usePlaySelectionMode,
  usePlayStackGap,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {SLIDE_TYPE_META} from '#/types/capsule'

/** Capsule accent for this block type — the fallback outside a book. */
const ACCENT = SLIDE_ACCENT.single_choice

export function SingleChoiceSlidePlay({
  slide,
  onAnswer,
  showFeedback,
  retryKey,
}: SlidePlayProps) {
  const {t: l} = useLingui()
  const p = useFlashBookPlayPalette()
  const d = useFlashBookPlayDesign()
  // Inside a book every selection affordance wears the book's own accent.
  const accent = usePlayAccent(ACCENT)
  // The book page supplies its own margins; capsule play keeps its 24px.
  const padding = usePlayContentPadding(24)
  // The answer row's shape comes from the theme: a ruled exercise line in a
  // notebook, a soft tinted row in a storybook, a bordered card in Academy.
  const optionStyle = usePlayOptionStyle()
  // On ruled paper the writing goes *on* the line, so the row and its spacing
  // come from the ruling rather than from the widget's own padding.
  const rowRhythm = usePlayOptionRowRhythm()
  const listStyle = usePlayOptionListStyle()
  const answerText = usePlayOptionTextStyle()
  // The space between the question and its answers is a whole number of the
  // page's rules, so the answers start on a line rather than 16px off one.
  const stackGap = usePlayStackGap(16)
  // A paper theme marks the answer with a highlighter stroke over the words
  // instead of filling the row: no box, no band, just ink on the writing.
  const highlight = usePlaySelectionMode() === 'highlight'
  const ink = useInkStroke()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [lastRetryKey, setLastRetryKey] = useState(retryKey)
  if (retryKey !== lastRetryKey) {
    setLastRetryKey(retryKey)
    setSelectedId(null)
  }

  const options = slide.options ?? []
  const solutionText = options.find(o => o.isCorrect)?.label

  /** The tone the marker and row should wear for this option right now. */
  const toneFor = (optId: string, isCorrect: boolean) => {
    const isSelected = selectedId === optId
    if (showFeedback) {
      if (isCorrect) return p.positive
      if (isSelected) return p.negative
      return p.muted
    }
    return isSelected ? accent : p.muted
  }

  const rowTint = (optId: string, isCorrect: boolean) => {
    const isSelected = selectedId === optId
    if (showFeedback) {
      if (isCorrect)
        return {borderColor: p.positive, backgroundColor: p.positive + '22'}
      if (isSelected && !isCorrect)
        return {borderColor: p.negative, backgroundColor: p.negative + '22'}
      return null
    }
    if (isSelected) return {borderColor: accent, backgroundColor: accent + '22'}
    return null
  }

  return (
    <View style={ss.root}>
      <PlayScroller
        style={ss.scroll}
        contentContainerStyle={{padding, gap: stackGap}}>
        <QuestionHeading label={SLIDE_TYPE_META[slide.type]?.label}>
          {slide.question}
        </QuestionHeading>

        <View style={[ss.optList, listStyle]}>
          {options.map((opt, i) => (
            <TouchableOpacity
              key={opt.id}
              style={[
                optionStyle,
                rowRhythm,
                // In highlight mode the stroke carries the state, so the row
                // keeps the paper showing through it.
                highlight ? null : rowTint(opt.id, opt.isCorrect),
              ]}
              onPress={() => {
                if (showFeedback) return
                setSelectedId(opt.id)
                onAnswer({
                  isCorrect: opt.isCorrect,
                  points: opt.isCorrect ? 1 : 0,
                  maxPoints: 1,
                  solutionText,
                  explanation: slide.explanation,
                  userAnswer: opt.id,
                })
              }}
              accessibilityRole="radio"
              accessibilityLabel={opt.label}
              accessibilityHint={l`Selects this answer`}
              accessibilityState={{selected: selectedId === opt.id}}>
              <OptionMarker
                index={i}
                tone={toneFor(opt.id, opt.isCorrect)}
                selected={
                  selectedId === opt.id || (showFeedback && opt.isCorrect)
                }
              />
              <Text style={[ss.optLabel, answerText, {color: p.body}]}>
                {/* The stroke goes on an inner run, not on the row's text box:
                    an inner `Text` is inline, so its background hugs the words
                    on each line instead of painting the full width of the row
                    — which is the difference between a highlighter and a band. */}
                <Text
                  style={ink(
                    selectedId === opt.id || (showFeedback && opt.isCorrect)
                      ? toneFor(opt.id, opt.isCorrect)
                      : null,
                  )}>
                  {opt.label}
                </Text>
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {/* The theme's own blue-ink rule closes the exercise. */}
        {d.block.mode === 'ruled' && (
          <View
            style={{
              height: StyleSheet.hairlineWidth * 2,
              backgroundColor: p.border,
              opacity: d.block.ruleOpacity,
            }}
          />
        )}
      </PlayScroller>
    </View>
  )
}

const ss = StyleSheet.create({
  root: {flex: 1},
  scroll: {flex: 1},
  optList: {gap: 10, marginTop: 4},
  optLabel: {flex: 1, fontSize: 15, lineHeight: 21},
})
